import { useState } from "react";
import { Check, Plus, Printer } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "./ui/card";

const checklists = {
  standard: [
    { room: "Kitchen", items: ["Wipe counters & cabinets", "Clean sink & faucet", "Wipe exterior appliances", "Clean microwave inside/out", "Sweep & mop floors", "Empty trash"] },
    { room: "Bathrooms", items: ["Clean & disinfect toilets", "Scrub showers & tubs", "Clean sinks & counters", "Clean mirrors", "Sweep & mop floors", "Empty trash"] },
    { room: "Living Areas", items: ["Dust all accessible surfaces", "Wipe tables & chairs", "Vacuum carpets & rugs", "Sweep & mop hard floors", "Tidy up general areas"] },
    { room: "Bedrooms", items: ["Dust surfaces & nightstands", "Make beds (if linens left out)", "Vacuum floors", "Empty trash", "Wipe mirrors"] },
  ],
  deep: [
    { room: "Everything in Standard, PLUS:", items: ["Wet wipe baseboards", "Clean interior windows & sills", "Dust ceiling fans & vents", "Wipe door frames & handles", "Extra attention to heavy buildup"] },
  ],
  moveInOut: [
    { room: "Everything in Deep Clean, PLUS:", items: ["Clean inside all cabinets", "Clean inside all drawers", "Clean inside refrigerator", "Clean inside oven", "Ensure home is move-in ready"] },
  ]
};

export const ChecklistVisualizer = () => {
  const [activeTab, setActiveTab] = useState<"standard" | "deep" | "moveInOut">("standard");

  return (
    <section className="py-24 bg-background">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto" id="printable-checklist">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Interactive Cleaning Checklist</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
            See exactly what's included in each of our professional cleaning packages.
          </p>
          <button 
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary font-bold rounded-lg hover:bg-primary/20 transition-colors mx-auto print:hidden"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12 print:hidden">
          <button
            onClick={() => setActiveTab("standard")}
            className={cn(
              "px-6 py-3 rounded-full font-semibold transition-all",
              activeTab === "standard" ? "bg-primary text-primary-foreground shadow-md" : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            Standard Clean
          </button>
          <button
            onClick={() => setActiveTab("deep")}
            className={cn(
              "px-6 py-3 rounded-full font-semibold transition-all",
              activeTab === "deep" ? "bg-primary text-primary-foreground shadow-md" : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            Deep Clean
          </button>
          <button
            onClick={() => setActiveTab("moveInOut")}
            className={cn(
              "px-6 py-3 rounded-full font-semibold transition-all",
              activeTab === "moveInOut" ? "bg-primary text-primary-foreground shadow-md" : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            Move In / Out
          </button>
        </div>

        {/* Print-only title to show which package is selected */}
        <div className="hidden print:block text-center mb-8">
          <h3 className="text-2xl font-bold text-primary border-b-2 border-primary inline-block pb-2">
            {activeTab === "standard" ? "Standard Clean" : activeTab === "deep" ? "Deep Clean" : "Move In/Out Clean"} Checklist
          </h3>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {checklists.standard.map((category, idx) => (
            <Card key={idx} className="p-6 border-2 border-muted">
              <h3 className="font-bold text-lg mb-4 text-primary border-b pb-2">{category.room}</h3>
              <ul className="space-y-3">
                {category.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-base">
                    <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
          
          {activeTab !== "standard" && checklists[activeTab === "deep" ? "deep" : "moveInOut"].map((category, idx) => (
             <Card key={`extra-${idx}`} className="p-6 border-2 border-primary bg-primary/5 md:col-span-2">
             <h3 className="font-bold text-lg mb-4 text-primary border-b border-primary/20 pb-2 flex items-center gap-2">
               <Plus className="w-5 h-5" /> {category.room}
             </h3>
             <ul className="grid md:grid-cols-2 gap-3">
               {category.items.map((item, i) => (
                 <li key={i} className="flex items-start gap-3 text-sm md:text-base font-medium">
                   <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                   <span>{item}</span>
                 </li>
               ))}
             </ul>
           </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
